import { LOCAL_STORAGE } from "@constants/index";
import { isLogoutInProgress } from "@auth/keycloakClient";
import { isLegacyAuth } from "@configs/authMode";
import { useKeycloakAuth } from "@auth/KeycloakAuthProvider";
import { PermissionContext } from "@contexts/PermissionContext";
import { RoutePermissionContext } from "@contexts/RoutePermissionContext";
import { HomeIcon } from "@heroicons/react/24/outline";
import { isAuthenticated } from "@utils/auth";
import { removeLocalStorage } from "@utils/local-storage";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../hooks/useTheme";

const ProtectedRoute = ({ children, viewPermissions = [], addPermissions = [], editPermissions = [], deletePermissions = [] }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const routePermissions = {
    view: viewPermissions,
    add: addPermissions,
    edit: editPermissions,
    delete: deletePermissions,
  };

  const { canView, canAdd, canEdit, canDelete } = useContext(PermissionContext);
  const { ready: keycloakReady, ensureAuthenticated } = useKeycloakAuth();
  const location = useLocation();
  const [authChecked, setAuthChecked] = useState(
    () => isLegacyAuth() && isAuthenticated(),
  );

  const handleGoToLogin = () => {
    removeLocalStorage([LOCAL_STORAGE.TOKEN, LOCAL_STORAGE.USER_ID]);
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    if (isLegacyAuth()) {
      if (!isAuthenticated()) {
        setAuthChecked(false);
        navigate("/login", { state: { from: location }, replace: true });
      } else {
        setAuthChecked(true);
      }
      return;
    }

    if (!keycloakReady) {
      return;
    }

    let cancelled = false;
    const guardRoute = async () => {
      const redirectPath = `${location.pathname}${location.search}` || "/dashboard";
      const authenticated = await ensureAuthenticated({ redirectPath });
      if (!authenticated) {
        removeLocalStorage([LOCAL_STORAGE.TOKEN, LOCAL_STORAGE.USER_ID]);

        if (!cancelled) {
          setAuthChecked(false);
          navigate("/login", { state: { from: location }, replace: true });
        }
        return;
      }

      if (!cancelled) {
        setAuthChecked(true);
      }
    };

    guardRoute();
    return () => {
      cancelled = true;
    };
  }, [keycloakReady, ensureAuthenticated, location, navigate]);

  if (!authChecked || isLogoutInProgress()) {
    return null;
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const hasViewPermission = canView(routePermissions);
  const hasAddPermission = canAdd(routePermissions);
  const hasEditPermission = canEdit(routePermissions);
  const hasDeletePermission = canDelete(routePermissions);

  if (!hasViewPermission && !hasAddPermission && !hasEditPermission && !hasDeletePermission) {
    return (
      <div className={`h-100 rounded-2xl p-8 ${colors.bgPrimary} ${colors.borderPrimary} flex flex-col items-center justify-center text-center`}>
        <p className={`text-base mb-8 ${colors.textSecondary}`}>
          {t("unauthorized.message", "You do not have permission to use the requested page.")}
        </p>

        <p className={`text-base mb-8 ${colors.textSecondary}`}>
          {t("unauthorized.instruction", "To continue working on the requested page, you must log in again with an account with appropriate permissions.")}
        </p>

        <div className="space-y-3">
          <button
            onClick={handleGoToLogin}
            className={`w-48 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-sm transition-colors ${colors.buttonTextPrimary} ${colors.buttonBackgroundGradient} hover:opacity-90`}
          >
            <HomeIcon className="h-5 w-5" />
            {t("unauthorized.go-login", "Go to Login")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <RoutePermissionContext.Provider
      value={{
        hasViewPermission,
        hasAddPermission,
        hasEditPermission,
        hasDeletePermission,
      }}
    >
      {children || <Outlet />}
    </RoutePermissionContext.Provider>
  );
};

const ClientConfigProtectedRoute = ({ children, clientConfigKey, allowedValues = [], redirectTo = "/dashboard" }) => {
  const location = useLocation();

  let clientConfigValue = null;

  if (clientConfigKey === "useImportComponent") {
    clientConfigValue = localStorage.getItem(LOCAL_STORAGE.USE_IMPORT_COMPONENT) || "Y";
  } else if (clientConfigKey === "usePreview") {
    clientConfigValue = localStorage.getItem(LOCAL_STORAGE.USE_PREVIEW) || "Y";
  }

  let hasAccess = true;

  if (allowedValues.length > 0) {
    hasAccess = allowedValues.some(
      (allowedValue) => clientConfigValue?.toLowerCase() === allowedValue?.toLowerCase(),
    );
  }

  if (!hasAccess) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return children || <Outlet />;
};

export { ClientConfigProtectedRoute };
export default ProtectedRoute;
